import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import InputFieldAddress from '../components/InputFieldAddress';
import DefinitionRow from '../components/DefinitionRow';
import ProductCodeAutocomplete from '../components/ProductCodeAutocomplete';
import useDataSelect from '../hooks/useDataSelect';
import { settingCompanyFields } from '../utils/fields';
import { Button, IconButton, Stack, Typography, TextField, Checkbox, FormControlLabel, Paper } from '@mui/material';
import { Sync, Close, Edit, Upload } from '@mui/icons-material';

const SettingCompanyPage = ({ hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const { register, handleSubmit, control, reset, setValue, formState: { errors } } = useForm({
        defaultValues: {
            show_stock: false, show_delivery_time: false, notice_text: '', logo_image_url: '', main_image_url: '',
            featured_product_1: '', featured_product_2: '', featured_product_3: '', featured_product_4: '', featured_product_5: '',
            company_name: '', postal_code: '', state: '', address_line: '', representative: '', additional_fees: '', defective_product_policy: '',
            return_shipping_policy: '', return_cancel_policy: '', delivery_time: '', payment_method: '', payment_deadline: '',
            contact_phone_number: '', contact_email_address: '', business_hours: '',
        },
    });
    const { dataset: settingData, error, refetch } = useDataSelect(`/setting/company`);
    const [isEditMode, setIsEditMode] = useState(false);
    const [logoImageFile, setLogoImageFile] = useState(null);
    const [mainImageFile, setMainImageFile] = useState(null);
    const [removeLogo, setRemoveLogo] = useState(false);
    const [removeMain, setRemoveMain] = useState(false);
    const logoPreviewUrl = React.useMemo(() => {
        if (!logoImageFile) return null;
        return URL.createObjectURL(logoImageFile);
    }, [logoImageFile]);

    const mainPreviewUrl = React.useMemo(() => {
        if (!mainImageFile) return null;
        return URL.createObjectURL(mainImageFile);
    }, [mainImageFile]);
    const currentLogoUrl =
        removeLogo
            ? null
            : logoPreviewUrl ||
            (settingData?.logo_image_url
                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${settingData.logo_image_url}`
                : null);

    const currentMainUrl =
        removeMain
            ? null
            : mainPreviewUrl ||
            (settingData?.main_image_url
                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${settingData.main_image_url}`
                : null);

    useEffect(() => {
        if (!settingData) return;
        reset({
            ...settingData,
            postal_code: settingData?.postal_code || '',
            state: settingData?.state || '',
            address_line: settingData?.address_line || '',
        });
        setLogoImageFile(null);
        setMainImageFile(null);
    }, [settingData, reset]);

    useEffect(() => {
        return () => {
            if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
        };
    }, [logoPreviewUrl]);

    useEffect(() => {
        return () => {
            if (mainPreviewUrl) URL.revokeObjectURL(mainPreviewUrl);
        };
    }, [mainPreviewUrl]);


    const onSubmit = async (data) => {
        try {
            const formData = new FormData();
            if (removeLogo) {
                formData.append('remove_logo', '1');
            }

            if (removeMain) {
                formData.append('remove_main', '1');
            }
            // 画像ファイルがあれば追加
            if (logoImageFile) { formData.append('logo_image', logoImageFile); }
            if (mainImageFile) { formData.append('main_image', mainImageFile); }

            // テキスト情報もフォームデータに入れる（全部文字列化）
            Object.entries(data).forEach(([key, value]) => {
                formData.append(key, value ?? '');
            });

            await axiosInstance.put('/setting/company', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            showSnackbar('情報が更新されました', 'success');
            refetch();
            setIsEditMode(false);

        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
        }
    };

    if (error) return <GlobalError errorMessage={error} />;
    return (
        <>
            <Typography variant="h1" component="h1">ECサイト設定</Typography>
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <Paper variant='section'>
                    <Typography variant="h4" component="h2">特定商取引法に基づく表示</Typography>
                    {/* 販売事業者名 */}
                    <DefinitionRow label="事業者名">
                        <TextField
                            name="company_name"
                            placeholder="事業者名を入力"
                            disabled={!isEditMode}
                            error={!!errors['company_name']}
                            helperText={errors['company_name']?.message}
                            {...register('company_name', { required: '会社名は必須です', maxLength: { value: 100, message: '会社名は100文字以内で入力してください' } })}
                            inputProps={{ maxLength: 100 }}
                        />
                    </DefinitionRow>
                    <DefinitionRow label="事業所所在地">
                        <InputFieldAddress
                            register={register}
                            errors={errors}
                            isEditMode={isEditMode}
                            setValue={setValue}
                            control={control}
                        />
                    </DefinitionRow>
                    {settingCompanyFields.map(({ label, name, type, placeholder, maxLength }) => (
                        <DefinitionRow key={name} label={label}>
                            <TextField
                                name={name}
                                type={type === 'textarea' ? undefined : type}
                                multiline={type === 'textarea'}
                                minRows={type === 'textarea' ? 3 : undefined}
                                placeholder={placeholder}
                                disabled={!isEditMode}
                                error={!!errors[name]}
                                helperText={errors[name]?.message}
                                inputProps={{ maxLength }}
                                {...register(name, {
                                    maxLength: {
                                        value: maxLength,
                                        message: `${label}は${maxLength}文字以内で入力してください`
                                    }
                                })}
                            />
                        </DefinitionRow>
                    ))}
                </Paper>
                <Paper variant='section'>
                    <Typography variant="h4" component="h2">ECサイト設定</Typography>
                    <DefinitionRow label="会社ロゴ画像">
                        <Stack alignItems="start" spacing={1}>
                            {currentLogoUrl && (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <img
                                        src={currentLogoUrl}
                                        alt="ロゴ画像プレビュー"
                                        style={{ maxHeight: 80, maxWidth: '100%' }}
                                    />
                                    {isEditMode && (
                                        <IconButton
                                            onClick={() => {
                                                setLogoImageFile(null);
                                                setValue('logo_image_url', null);
                                                setRemoveLogo(true);
                                            }}
                                            style={{ position: 'absolute', top: 0, right: 0 }}
                                        >
                                            <Close />
                                        </IconButton>
                                    )}
                                </div>
                            )}

                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<Upload />}
                                disabled={!isEditMode}
                            >
                                画像を選択
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        setLogoImageFile(file || null);
                                        setRemoveLogo(false);
                                    }}
                                />
                            </Button>
                        </Stack>
                    </DefinitionRow>

                    <DefinitionRow label="ECサイト メイン画像">
                        <Stack alignItems="start" spacing={1}>
                            {currentMainUrl && (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <img
                                        src={currentMainUrl}
                                        alt="メイン画像プレビュー"
                                        style={{ maxHeight: 200, maxWidth: '100%' }}
                                    />
                                    {isEditMode && (
                                        <IconButton
                                            onClick={() => {
                                                setMainImageFile(null);
                                                setValue('main_image_url', null);
                                                setRemoveMain(true);
                                            }}
                                            style={{ position: 'absolute', top: 0, right: 0 }}
                                        >
                                            <Close />
                                        </IconButton>
                                    )}
                                </div>
                            )}
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<Upload />}
                                disabled={!isEditMode}
                            >
                                画像を選択
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        setMainImageFile(file || null);
                                        setRemoveMain(false);
                                    }}
                                />
                            </Button>
                        </Stack>
                    </DefinitionRow>
                    <DefinitionRow label="在庫有無を表示しますか">
                        <Controller
                            name="show_stock"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value || false}
                                            disabled={!isEditMode}
                                        />
                                    }
                                    label="表示する"
                                />
                            )}
                        />
                    </DefinitionRow>
                    <DefinitionRow label="発送までの日数を表示しますか">
                        <Controller
                            name="show_delivery_time"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value || false}
                                            disabled={!isEditMode}
                                        />
                                    }
                                    label="表示する"
                                />
                            )}
                        />
                    </DefinitionRow>
                    {[1, 2, 3, 4, 5].map(i => (
                        <DefinitionRow key={`featured_product_${i}`} label={`イチオシ商品${i}`}>
                            <Controller
                                name={`featured_product_${i}`}
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: 100,
                                        message: '100文字以内で入力してください',
                                    },
                                }}
                                render={({ field }) => (
                                    <ProductCodeAutocomplete
                                        label="商品コード"
                                        value={field.value || ''}
                                        isEditMode={isEditMode}
                                        fetchSuggestions={async (keyword) => {
                                            try {
                                                const res = await axiosInstance.post('/product/product_code_suggest', { keyword });
                                                return res.data; // [{ product_code, product_name }]
                                            } catch (err) {
                                                console.error(err);
                                                return [];
                                            }
                                        }}
                                        onChange={(val) => field.onChange(val)}
                                        error={!!errors[`featured_product_${i}`]}
                                        helperText={errors[`featured_product_${i}`]?.message}
                                    />
                                )}
                            />
                        </DefinitionRow>
                    ))}                </Paper >
                <Paper variant='section'>
                    <Typography variant="h4" component="h2">取引先による新規登録時に表示する注意事項（取引条件等）</Typography>
                    <DefinitionRow label="注意事項">
                        <TextField
                            multiline
                            minRows={8}
                            name="notice_text"
                            placeholder="注意事項を入力してください"
                            disabled={!isEditMode}
                            error={!!errors.notice_text}
                            helperText={errors.notice_text?.message}
                            {...register('notice_text', { maxLength: { value: 5000, message: '注意事項は5000文字以内で入力してください' } })}
                            inputProps={{ maxLength: 5000 }}
                        />
                    </DefinitionRow>
                </Paper>
                <div className="button-row">
                    <Button
                        variant='outlined'
                        startIcon={isEditMode ? <Close /> : <Edit />}
                        onClick={async () => {
                            if (isEditMode) {
                                await refetch();
                            }
                            setIsEditMode(prev => !prev);
                        }}
                    >
                        {isEditMode ? '編集を破棄する' : '内容を編集する'}
                    </Button>
                    {isEditMode && (
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={<Sync />}
                        >
                            内容を更新する
                        </Button>
                    )}
                </div>
            </form >
        </>
    );
};

export default SettingCompanyPage;
